import { useEffect, useRef, useCallback } from 'react';
import { useAppSelector } from '@/store';
import { getApiBaseUrl } from '@/utils/apiBase';
import { getTenantSlug } from '@/utils/tenant';

/**
 * Consumes the SSE response using fetch + Authorization (and X-Tenant-Slug),
 * since EventSource cannot set headers. Avoids long JWTs in query strings
 * (proxy limits) and matches the same auth as other API calls.
 */
async function consumeNotificationSse(
  token: string,
  signal: AbortSignal,
  onNotification: () => void,
): Promise<'ok' | 'unauthorized' | 'error'> {
  const base = getApiBaseUrl();
  const tenantSlug = getTenantSlug();
  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
    Accept: 'text/event-stream',
  };
  if (tenantSlug) {
    headers['X-Tenant-Slug'] = tenantSlug;
  }

  let res: Response;
  try {
    res = await fetch(`${base}/notifications/stream`, { method: 'GET', headers, signal });
  } catch {
    return 'error';
  }

  if (res.status === 401 || res.status === 403) return 'unauthorized';
  if (!res.ok || !res.body) return 'error';

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  const processBlocks = (text: string) => {
    const parts = text.split('\n\n');
    const rest = parts.pop() ?? '';
    for (const part of parts) {
      let eventName = '';
      for (const line of part.split('\n')) {
        if (line.startsWith('event:')) {
          eventName = line.slice(6).trim();
        }
      }
      if (eventName === 'notification') {
        onNotification();
      }
    }
    return rest;
  };

  try {
    while (!signal.aborted) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      buffer = processBlocks(buffer);
    }
  } catch {
    return 'error';
  }

  return 'ok';
}

/**
 * Server-Sent Events for /api/v1/notifications/stream (notification + ping heartbeats).
 * Uses fetch + Bearer auth; reconnects with exponential back-off on errors / disconnect.
 */
export function useNotificationStream(
  onNotification: () => void,
  options?: { enabled?: boolean },
) {
  const enabled = options?.enabled ?? true;
  const token = useAppSelector((state) => state.auth.accessToken);
  const retryDelayRef = useRef(3000);
  const onNotificationRef = useRef(onNotification);
  onNotificationRef.current = onNotification;

  const connectLoop = useCallback(
    async (signal: AbortSignal) => {
      let delay = retryDelayRef.current;
      while (!signal.aborted) {
        if (!token || !enabled) return;

        const result = await consumeNotificationSse(token, signal, () => onNotificationRef.current());

        if (signal.aborted) return;

        if (result === 'ok') {
          retryDelayRef.current = 3000;
          delay = retryDelayRef.current;
        } else {
          delay = Math.min(delay * 2, 30000);
          retryDelayRef.current = delay;
        }

        await new Promise<void>((resolve) => {
          const t = setTimeout(resolve, delay);
          signal.addEventListener('abort', () => clearTimeout(t));
        });
      }
    },
    [token, enabled],
  );

  useEffect(() => {
    retryDelayRef.current = 3000;
  }, [token]);

  useEffect(() => {
    const ac = new AbortController();
    connectLoop(ac.signal);
    return () => ac.abort();
  }, [connectLoop]);
}
